import Calculator, {
  type BikeTypeV2,
  type FrameContractDTO,
  type LeasingOptionType,
} from "@jobrad-gmbh/bike-leasing-calculator";
import { BudgetCalculationActionTypes } from "./actions";
import type { BudgetCalculationState, BudgetCalculatorInputDTO } from "./types";
import type { AppAction, CalculatorEnv } from "..";
import { getBikeTypeOptions, getServiceOptions } from "../frameContractUtils";

const DEFAULT_MONTHLY_BUDGET = 100;

const initialBudgetCalculationState: BudgetCalculationState = {
  calculator: null,
  userInput: {},
  defaultInput: {
    monthlyBudgetBrutto: DEFAULT_MONTHLY_BUDGET,
  },
  result: null,
  serviceOptions: [],
  bikeTypeOptions: [],
  hasCalculated: false,
  elementVisibility: {
    insurancePayerSelect: false,
    grantInput: false,
    bikeTypeSelect: false,
    serviceSelect: false,
  },
};

const budgetCalculationReducer = (state: BudgetCalculationState, action: AppAction): BudgetCalculationState => {
  switch (action.type) {
    case BudgetCalculationActionTypes.BUDGET_INIT: {
      const { frameContract, calculatorEnv } = action.payload;
      const calculator = new Calculator(frameContract);
      const serviceOptions = getServiceOptions(frameContract);
      const bikeTypeOptions = getBikeTypeOptions(frameContract);
      const defaultInput = getDefaultBudgetInput(frameContract, bikeTypeOptions, serviceOptions, calculatorEnv);
      const isServicesV2 = frameContract.bike_services.services_version !== "1.0";

      return {
        ...state,
        calculator,
        defaultInput,
        serviceOptions,
        bikeTypeOptions,
        userInput: {},
        result: null,
        hasCalculated: false,
        elementVisibility: {
          insurancePayerSelect: frameContract.insurance_option_id.payer === "selectable",
          grantInput: frameContract.grant_type === "individual",
          bikeTypeSelect: isServicesV2 && bikeTypeOptions.length > 1,
          serviceSelect: serviceOptions.length > 1,
        },
      };
    }

    case BudgetCalculationActionTypes.BUDGET_UPDATE_INPUT: {
      return {
        ...state,
        userInput: { ...state.userInput, ...action.payload },
      };
    }

    case BudgetCalculationActionTypes.BUDGET_CALCULATE: {
      if (!state.calculator) {
        return state;
      }
      const mergedInput: BudgetCalculatorInputDTO = {
        ...state.defaultInput,
        ...state.userInput,
      };
      if (!mergedInput.monthlyBudgetBrutto || mergedInput.monthlyBudgetBrutto <= 0) {
        return { ...state, result: null, hasCalculated: false };
      }
      const result = state.calculator.calculateMaxBikePrice(mergedInput);
      return { ...state, result, hasCalculated: true };
    }

    case BudgetCalculationActionTypes.BUDGET_RESET: {
      return { ...state, result: null, hasCalculated: false };
    }

    default:
      return state;
  }
};

function getDefaultBudgetInput(
  frameContract: FrameContractDTO,
  bikeTypeOptions: BikeTypeV2[],
  serviceOptions: LeasingOptionType[],
  calculatorEnv: CalculatorEnv,
): BudgetCalculatorInputDTO {
  const serviceType = serviceOptions.length ? serviceOptions[0].service_type : false;
  const serviceSubType = serviceOptions.length ? serviceOptions[0].service_subtype : false;

  const serviceZahler =
    calculatorEnv === "website" || calculatorEnv === "dealer" ? "employee" : frameContract.bike_services.payer.value;

  const versicherungsZahler =
    frameContract.insurance_option_id.payer === "selectable" ? "company" : frameContract.insurance_option_id.payer;

  const vorsteuerabzugsberechtigt = frameContract.use_input_tax_deduction !== "no";

  const arbeitgeberZuschuss = deriveDefaultGrant(frameContract);

  return {
    monthlyBudgetBrutto: DEFAULT_MONTHLY_BUDGET,
    serviceType,
    serviceSubType,
    serviceZahler,
    versicherungsZahler,
    fahrradtyp: bikeTypeOptions.includes("e_bike") ? "e_bike" : (bikeTypeOptions[0] ?? "e_bike"),
    vorsteuerabzugsberechtigt,
    arbeitgeberZuschuss,
  };
}

function deriveDefaultGrant(frameContract: FrameContractDTO): number {
  if (frameContract.grant_type === "fixed_amount" || frameContract.grant_type === "fixed_percent") {
    return frameContract.grant;
  }
  return 0;
}

export type { BudgetCalculationState } from "./types";
export { budgetCalculationReducer, initialBudgetCalculationState };
